import { UserType } from 'src/type/user';

import { yupResolver } from '@hookform/resolvers/yup';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { LoadingButton } from '@mui/lab';
import {
  Grid,
  Stack,
  Dialog,
  Button,
  Avatar,
  MenuItem,
  Typography,
  DialogTitle,
  ListItemText,
  DialogContent,
  DialogActions,
  InputAdornment,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useForm, Controller } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField, RHFUploadBox } from 'src/components/hook-form';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import { Dayjs } from 'dayjs';
import { useState, useCallback } from 'react';
import { fData } from 'src/utils/format-number';
import { uploadImage } from 'src/libs/firebase/firebase-upload';
import axios, { endpoints } from 'src/utils/axios';
import useBankAccountOptions from 'src/hooks/use-bank-account-options';
import {
  GoldWithdrawFilters,
  GoldWithdrawFilterKey,
  GoldWithdrawFilterValue,
} from 'src/type/gold-withdraw';
import UserSearchAutocomplete, { UserOption } from 'src/components/ui-kit/user-search-autocomplete';
import useCurrentDateTime from 'src/hooks/use-current-date-time';

const defaultFilters: GoldWithdrawFilters = {
  name: null,
  productId: 'all',
  startDate: null,
  endDate: null,
  status: 'all',
};

const defaultValues = {
  creditAmount: 0,
  paymentImage: null as unknown as File,
  paidAtDate: null as unknown as Dayjs,
  paidAtTime: null as unknown as Dayjs,
  adminBankAccountId: '',
};

const Schema = Yup.object().shape({
  creditAmount: Yup.number()
    .typeError('จำนวนเงินต้องเป็นตัวเลข')
    .required('จำเป็นต้องระบุจำนวนเงิน')
    .moreThan(-1, 'จำนวนเงินขั้นต่ำ 0'),
  paymentImage: Yup.mixed<File>().required('กรุณาแนบหลักฐานการชำระเงิน'),
  paidAtDate: Yup.mixed<Dayjs>().required('กรุณากรอกวันที่โอน'),
  paidAtTime: Yup.mixed<Dayjs>().required('กรุณากรอกเวลาที่โอน'),
  adminBankAccountId: Yup.string().required('กรุณาเลือกบัญชีธนาคาร'),
});

type Props = {
  open: boolean;
  onClose: VoidFunction;
  user: UserType | null;
  isCreateAndApprove?: boolean;
  handleReload: VoidFunction;
  openApproveDialog?: (txId?: string) => void;
};

export default function TopupCreateDialog({
  open,
  onClose,
  user,
  isCreateAndApprove = false,
  handleReload,
  openApproveDialog,
}: Props) {
  const currentDateTime = useCurrentDateTime();
  const [filters, setFilters] = useState(defaultFilters);

  const handleFilters = useCallback(
    (name: GoldWithdrawFilterKey, value: GoldWithdrawFilterValue) => {
      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    []
  );

  const handleFilterName = useCallback(
    (userOption: UserOption | null) => {
      handleFilters('name', userOption);
    },
    [handleFilters]
  );

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    control,

    formState: { isSubmitting },
  } = methods;

  const { bankAccountOptions } = useBankAccountOptions();

  const onSubmit = handleSubmit(async (values) => {
    try {
      const paymentImageUrl = await uploadImage(values.paymentImage, 'images');

      values.paidAtDate
        .set('hour', values.paidAtTime.hour())
        .set('minute', values.paidAtTime.minute());

      const uid = user?.uid || filters.name?.value;

      if (!uid) {
        throw new Error('ไม่พบข้อมูลลูกค้า');
      }

      const body = {
        uid,
        creditAmount: Number(values.creditAmount),
        paymentImageUrl,
        paidAt: values.paidAtDate.toISOString(),
        adminBankAccountId: values.adminBankAccountId,
        currentDateTime,
      };

      const { data } = await axios.post(endpoints.transactions.topupDeposit.create, body);
      if (isCreateAndApprove && openApproveDialog && data) {
        openApproveDialog(data.id);
      }

      if (!isCreateAndApprove) {
        handleReload();
        enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
      }

      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const handleDropImage = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });

      if (file) {
        setValue('paymentImage', newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );

  // console.log(bankAccountOptions);

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>สร้างรายการวงเงินหลักประกัน</DialogTitle>

        <DialogContent>
          <Stack spacing={3} py={1}>
            {(!user || user === null) && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                flexGrow={1}
                sx={{ width: { xs: 1, md: 'auto' }, minWidth: 140 }}
              >
                <UserSearchAutocomplete
                  label="ค้นหาด้วยชื่อลูกค้า"
                  value={filters.name}
                  onChange={handleFilterName}
                />
              </Stack>
            )}

            <RHFTextField
              name="creditAmount"
              type="number"
              label="จำนวนเงิน"
              placeholder="0"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography fontWeight="bold" color="success.main">
                      THB
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />

            <RHFUploadBox
              name="paymentImage"
              maxSize={3145728}
              onDrop={handleDropImage}
              placeholder="อัพโหลดรูปหลักฐาน"
              sx={{
                maxHeight: 140,
                width: '100%',
                height: '100%',
                aspectRatio: '1/1',
                mx: 'auto',
              }}
              helperText={
                <>
                  อนุญาตไฟล์ *.jpeg, *.jpg, *.png, *.gif
                  <br /> ขนาดสูงสุด {fData(3145728)}
                </>
              }
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Stack>
                  <Controller
                    name="paidAtDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label="วันที่โอน"
                        value={getValues('paidAtDate')}
                        onChange={(newValue) => {
                          field.onChange(newValue);
                        }}
                        format="DD/MM/YYYY"
                        sx={{ width: 1 }}
                      />
                    )}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="paidAtTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label="เวลาที่โอน"
                      ampm={false}
                      format="HH:mm"
                      value={getValues('paidAtTime')}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      sx={{ width: 1 }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <RHFSelect name="adminBankAccountId" label="บัญชีธนาคาร" placeholder="เลือกบัญชีธนาคาร">
              {bankAccountOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={!option.info.isEnable}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar alt="" src={option?.info?.bank?.imageUrl}>
                      {option?.info?.bank?.name.charAt(0).toUpperCase()}
                    </Avatar>

                    <ListItemText
                      disableTypography
                      primary={
                        <Typography variant="body2" noWrap>
                          {option?.info?.bank?.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {option.info.accountName}
                        </Typography>
                      }
                    />
                  </Stack>
                </MenuItem>
              ))}
            </RHFSelect>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton
            variant="contained"
            type="submit"
            disabled={isCreateAndApprove && !filters.name?.value && !user?.uid}
            loading={isSubmitting}
          >
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
